import { AlkLogger } from '@alk/core';
import { CcypairToCurrencyPair, ICcypair } from '@alk/currency';
import {
    convertFromProtobufExpiryType,
    convertFromProtobufMarketCloseness,
    convertFromProtobufOrderReportType,
    convertFromProtobufOrderSide,
    convertFromProtobufOrderSpotType,
    convertFromProtobufOrderState,
    convertFromProtobufPendingStrategy,
    convertFromProtobufPriceType,
} from '../../../helpers/protobuf';
import { DeepRequired, FlatRequired } from '../../../types';
import {
    IBlotterOrderUpdate,
    IBlotterTransaction,
    IOrderReport,
    ORDER_KEY,
} from '../../../interfaces';
import {
    IOrder,
    IOrdersRequest,
    IOrdersResponse,
    IOrderUpdate,
    OrdersRequest,
} from 'sfx-web-contract';
import { ISO8601 } from '@alk/dates';
import { mapperFabric } from '../../../helpers/automapper';
import { TransformAction } from '../../../interfaces/functions.interface';

export const orderMapper: TransformAction<FlatRequired<IOrder>, FlatRequired<IOrderReport>> =
    mapperFabric<FlatRequired<IOrder>, FlatRequired<IOrderReport>>()
        .map('OrderInstrument', (inst) => ({ orderType: convertFromProtobufOrderReportType(inst) }))
        .map('OrderId', (orderId) => ({ orderId }))
        .map('KondorDealTypeID', (dealNumber) => ({ dealNumber: dealNumber?.value }))
        .map('State', (state) => ({ state: convertFromProtobufOrderState(state) }))
        .map('Side', (side) => ({ side: convertFromProtobufOrderSide(side) }))
        .map('FillPercent', (fillPercent) => ({ fillPercent }))
        .map('Symbol', (symbol) => ({ instrument: CcypairToCurrencyPair(symbol as ICcypair) }))
        .map('BaseCurrency', (baseCurrency) => ({ baseCurrency }))
        .map('Type', (type) => ({ spotType: convertFromProtobufOrderSpotType(type) }))
        .map('CreatedBy', (traderId) => ({ traderId }))
        .map('TraderName', (traderName) => ({ traderName }))
        .map('ClientName', (clientName) => ({ clientName }))
        .map('ClientId', (clientId) => ({ clientId }))
        .map('ExtRefUserId', (userExtRefId) => ({ userExtRefId }))
        .map('ExtRefClientId', (clientExtRefId) => ({ clientExtRefId }))
        .map('RequestedAmount', (requestedAmount) => ({ requestedAmount }))
        .map('LeftAmount', (leftAmount) => ({ leftAmount }))
        .map('CounterFilledAmount', (counterFilledAmount) => ({ counterFilledAmount }))
        .map('CounterCurrency', (counterCurrency) => ({ counterCurrency }))
        .map('FilledAmount', (filledAmount) => ({ filledAmount }))
        .map('RequestedPrice', (requestedPrice) => ({ requestedPrice }))
        .map('FilledPrice', (filledPrice) => ({ filledPrice }))
        .map('CreatedDate', (tradeDate) => ({ tradeDate: tradeDate as ISO8601 }))
        .map('NearValueDate', (valueDate) => ({
            valueDate: valueDate?.value as ISO8601,
            nearValueDate: valueDate?.value as ISO8601,
        }))
        .map('UpdateDate', (updateDate) => ({ updateDate: updateDate as ISO8601 }))
        .map('ExecutionDate', (executionDate) => ({ executionDate: executionDate?.value as ISO8601 }))
        .map('NearAmount', (nearAmount) => ({ nearAmount }))
        .map('NearPoints', (nearPoints) => ({ nearPoints }))
        .map('NearPrice', (nearPrice) => ({ nearPrice }))
        .map('FarAmount', (farAmount) => ({ farAmount: farAmount?.value }))
        .map('FarPoints', (farPoints) => ({ farPoints: farPoints?.value }))
        .map('FarPrice', (farPrice) => ({ farPrice: farPrice?.value }))
        .map('FarValueDate', (farValueDate) => ({ farValueDate: farValueDate?.value as ISO8601 }))
        .map('Expiry', (expiry) => ({ expiryType: convertFromProtobufExpiryType(expiry) }))
        .map('ExpiryDate', (expiryDate) => ({ expiryDate: expiryDate?.value as ISO8601 }))
        .map('MarketCloseness', (marketCloseness) => ({ marketCloseness: convertFromProtobufMarketCloseness(marketCloseness) }))
        .map('MarketDistance', (marketDistance) => ({ marketDistance: marketDistance?.value }))
        .map('MarketPoints', (marketPoints) => ({ marketPoints: marketPoints?.value }))
        .map('FarCounterFilledAmount', (farCounterFilledAmount) => ({ farCounterFilledAmount: farCounterFilledAmount?.value }))
        .map('SpotRate', (spotRate) => ({ spotRate: spotRate?.value }))
        .map('PendingStrategyId', (pendingStrategyId) => ({ pendingStrategyId: pendingStrategyId?.value }))
        .map('PendingLegType', (pendingLegType) => ({ pendingLegType: convertFromProtobufPriceType(pendingLegType) }))
        .map('PendingStrategy', (pendingStrategy) => ({ pendingStrategy: convertFromProtobufPendingStrategy(pendingStrategy) }))
        .result;

const gatewayOrderUpdateMapper:
Record<
keyof Omit<IOrderUpdate, 'OrderId'>,
TransformAction<IOrderUpdate, Partial<IOrderReport>>> = {
    FillPercent: (gUpdate) => ({ fillPercent: gUpdate.FillPercent?.value }),
    FilledAmount: (gUpdate) => ({ filledAmount: gUpdate.FilledAmount?.value }),
    FilledPrice: (gUpdate) => ({ filledPrice: gUpdate.FilledPrice?.value }),
    LeftAmount: (gUpdate) => ({ leftAmount: gUpdate.LeftAmount?.value }),
    UpdateDate: (gUpdate) => ({ updateDate: gUpdate.UpdateDate?.value as ISO8601 }),
    State: (gUpdate) => ({ state: Boolean(gUpdate.State) ?
        convertFromProtobufOrderState(gUpdate.State.Value) :
        undefined }),
    RequestedPrice: (gUpdate) => ({ requestedPrice: gUpdate.RequestedPrice?.value }),
    RequestedAmount: (gUpdate) => ({ requestedAmount: gUpdate.RequestedAmount?.value }),
    MarketPoints: (gUpdate) => ({ marketPoints: gUpdate.MarketPoints?.value }),
    MarketDistance: (gUpdate) => ({ marketDistance: gUpdate.MarketDistance?.value }),
    MarketCloseness: (gUpdate) => ({ marketCloseness: Boolean(gUpdate.MarketCloseness) ?
        convertFromProtobufMarketCloseness(gUpdate.MarketCloseness.Value) :
        undefined }),
    ExecutionDate: (gUpdate) => ({ executionDate: gUpdate.ExecutionDate?.value as ISO8601 }),
    CounterFilledAmount: (gUpdate) => ({ counterFilledAmount: gUpdate.CounterFilledAmount?.value }),
    CounterCurrency: (gUpdate) => ({ counterCurrency: gUpdate.CounterCurrency?.value }),
    ValueDate: (gUpdate) => ({ valueDate: gUpdate.ValueDate?.value as ISO8601 }),
    KondorDealTypeID: (gUpdate) => ({ dealNumber: gUpdate.KondorDealTypeID?.value }),
};

export const orderUpdateMapper = (update: IOrderUpdate): IBlotterOrderUpdate =>
    Object.keys(gatewayOrderUpdateMapper)
        .reduce(
            (acc, cur) => ({ ...acc, ...gatewayOrderUpdateMapper[ cur ](update) }),
            { orderId: update.OrderId },
        );

export const ordersTransformator =
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ({ DeletedOrdersIds, NewOrders, TotalAmount: total, UpdatedOrders }: IOrdersResponse): IBlotterTransaction => ({
        newItems: NewOrders?.map(orderMapper) ?? [],
        deletedKeys: DeletedOrdersIds ?? [],
        updated: UpdatedOrders?.map(orderUpdateMapper) ?? [],
        total,
        key: ORDER_KEY,
    });

export function validateSpotOrder(order: IOrder): null | Error {

    //TODO: move ISO parser to @alk/dates
    //TODO: use general validator of property to generate error message
    if (isNaN(Date.parse(order.CreatedDate))) {
        return Error('[BLOTTER_ADAPTER_SERVICE_UTILS#validateSpotOrder] order \'CreatedDate\' is not in ISO8601 date format');
    }

    if (Boolean(order.NearValueDate) && isNaN(Date.parse(order.NearValueDate.value))) {
        return Error('[BLOTTER_ADAPTER_SERVICE_UTILS#validateSpotOrder] order \'NearValueDate\' is not in ISO8601 date format');
    }

    if (isNaN(Date.parse(order.UpdateDate))) {
        return Error('[BLOTTER_ADAPTER_SERVICE_UTILS#validateSpotOrder] order \'UpdateDate\' is not in ISO8601 date format');
    }

    return null;
}

export const createOrdersRequest = (clientId: number, chunkSize: number): Uint8Array => {

    const fullRequest: DeepRequired<IOrdersRequest> = {
        ClientId: clientId,
        ChunkSize: chunkSize,
    };

    const request = OrdersRequest.create(fullRequest);

    AlkLogger.debug(`[BLOTTER_ADAPTER_SERVICE#createRequest] is about to create ${ ''
    }subscription for: ${ JSON.stringify(request.toJSON()) }`);

    return OrdersRequest.encode(request).finish();
};
